import { customerSettingsFragment, orgAddressFragment, orgContactFragment } from 'apollo/fragments';
import { clientContentFragment, invoiceSettingsFragment, orgCurrentJobFragment } from 'apollo/fragments/organization';
import { gql } from 'graphql.macro';

export const getLabClientsTemplate = gql`
    query labClients($limit: Int, $offset: Int, $groupedByParent: Boolean) {
        labClients(limit: $limit, offset: $offset, groupedByParent: $groupedByParent) {
            _id
            invoiceEnabled
            ptjCustomerNumber
            transportProvider {
                channel
                externalId
                code
            }
            customerId
            isActive
            customer {
                _id
                active
                materialListName
                actionListName
                name
                type
                ptjCustomerNumber
                isWhiteLabeled
                organizationNumber
                ptjCustomerNumber
                isMain
                customerType
                isTransportEnabled
                parentClinicId
                parentClinic {
                    name
                }
            }
        }
    }
`;

export const getSuppliersTemplate = gql`
    query suppliers($limit: Int, $offset: Int) {
        suppliers(limit: $limit, offset: $offset) {
            _id
            customerId
            lab {
                _id
                name
                type
                active
                customerType
                contact {
                    address {
                        ...orgAddressFields
                    }
                    communication {
                        phone
                        email
                        site
                    }
                }
                openingHours {
                    day
                    time {
                        from
                        to
                    }
                    active
                }
                holidayDays
                currentJobs {
                    ...orgCurrentJobFields
                }
                customerSettings {
                    ...customerSettingsFields
                }
            }
        }
    }
    ${orgAddressFragment}
    ${orgCurrentJobFragment}
    ${customerSettingsFragment}
`;

export const getLabClientByIdTemplate = gql`
    query labClientById($customerId: MongoDbObjectId!) {
        labClientById(customerId: $customerId) {
            _id
            transportProvider {
                channel
                externalId
                code
            }
            externalDeliveryDaysAdvance
            internalDeliveryDaysAdvance
        }
    }
`;

export const getParentChildLabClientComboTemplate = gql`
    query parentChildLabClientCombo($parentId: MongoDbObjectId!, $childId: MongoDbObjectId!) {
        parentChildLabClientCombo(parentId: $parentId, childId: $childId) {
            _id
            isActive
            invoiceEnabled
            useOwnInvoiceSettings
            invoiceSettings {
                ...invoiceSettingsFields
            }
            customerId
            transportProvider {
                externalId
                code
                channel
            }
            ptjCustomerNumber
        }
    }
    ${invoiceSettingsFragment}
`;

export const labClientTemplate = gql`
    query labClient($clientId: MongoDbObjectId!) {
        labClient(clientId: $clientId) {
            _id
            labId
            isWriteAllowed
            parentHasInvoiceConfig
            useOwnInvoiceSettings
            customerId
            isActive
            actionBasedCurrency
            customer {
                _id
                organizationNumber
                type
                orderVersion
                ptjCustomerNumber
                parentClinicId
                name
                officialName
                gln
                isNotPickUpAndDeliveryPlace
                isWhiteLabeled
                customerType
                isMain
                invoicing {
                    vatNo
                    costCenter
                    communication {
                        email
                    }
                    invoiceAddress {
                        ...orgAddressFields
                    }
                }
                contact {
                    address {
                        ...orgAddressFields
                    }
                    communication {
                        phone
                        email
                        site
                    }
                }
                customerSettings {
                    ...customerSettingsFields
                }
                parentClinicId
                parentClinic {
                    name
                    customerSettings {
                        ...customerSettingsFields
                    }
                }
                clinicInternalNotes {
                    lab
                    note
                }
            }
            invoiceEnabled
            ptjCustomerNumber
            internalDeliveryDaysAdvance
            externalDeliveryDaysAdvance
            clientContent {
                ...clientContentFields
            }
            invoiceSettings {
                ...invoiceSettingsFields
            }
            transportProvider {
                externalId
                code
                channel
            }
            deliveryInstructions
            clinicTransportPhone
            accountManager
            settings {
                sendDeliveryNotesByEmail
                deliveryNotesEmailType
                sharedEmails
            }
            localeFormat {
                language
                date
                number
            }
        }
    }
    ${orgContactFragment}
    ${clientContentFragment}
    ${invoiceSettingsFragment}
    ${customerSettingsFragment}
    ${orgAddressFragment}
`;

export const getSupplierTemplate = gql`
    query supplier($supplierId: MongoDbObjectId!) {
        supplier(supplierId: $supplierId) {
            _id
            customerId
            isWriteAllowed
            labId
            lab {
                _id
                organizationNumber
                type
                orderVersion
                customerType
                ptjCustomerNumber
                name
                officialName
                gln
                contact {
                    address {
                        ...orgAddressFields
                    }
                    communication {
                        phone
                        email
                        site
                    }
                }
            }
            ptjCustomerNumber
            internalDeliveryDaysAdvance
            externalDeliveryDaysAdvance
            clientContent {
                ...clientContentFields
            }
        }
    }
    ${clientContentFragment}
    ${orgAddressFragment}
`;

export const actionPriceListForClientTemplate = gql`
    query actionPriceListForClient($clientId: MongoDbObjectId!) {
        actionPriceListForClient(clientId: $clientId) {
            _id
            actionPriceListName
            currency
            generalDiscount
            isUsingDefault
            actions {
                _id
                action
                actionCode
                description
                addedDate
                category
                basePrice
                tiotandvardPrice
                discountedPrice
                customerSpecificTiotandvardPrice
                price
            }
        }
    }
`;

export const actionPriceListForOrderTemplate = gql`
    query actionPriceListForClient($clientId: MongoDbObjectId!) {
        actionPriceListForClient(clientId: $clientId) {
            _id
            actionPriceListName
            currency
            generalDiscount
            actions {
                _id
                action
                actionCode
                internalCode
                description
                vat
                addedDate
                category
                basePrice
                tiotandvardPrice
                discountedPrice
                customerSpecificTiotandvardPrice
                price
                materialMacros {
                    _id
                    name
                    materials {
                        _id
                        material {
                            _id
                            materialCode
                            description
                            vat
                            batchInventoryManagement
                            batches {
                                batchNumber
                                quantity
                                fullBatchQuantity
                                createdOn
                                closed
                                closedDate
                            }
                        }
                    }
                }
            }
        }
    }
`;

export const materialPriceListForClientTemplate = gql`
    query materialPriceListForClient($clientId: MongoDbObjectId!) {
        materialPriceListForClient(clientId: $clientId) {
            _id
            materialPriceListName
            currency
            generalDiscount
            isUsingDefault
            materials {
                _id
                material
                materialCode
                description
                addedDate
                basePrice
                discountedPrice
                price
            }
        }
    }
`;

export const materialPriceListForOrderTemplate = gql`
    query materialPriceListForClient($clientId: MongoDbObjectId!) {
        materialPriceListForClient(clientId: $clientId) {
            _id
            materialPriceListName
            currency
            generalDiscount
            materials {
                _id
                material
                materialCode
                description
                vat
                addedDate
                basePrice
                discountedPrice
                price
                batchInventoryManagement
                batches {
                    batchNumber
                    quantity
                    fullBatchQuantity
                    createdOn
                    closed
                    closedDate
                }
            }
        }
    }
`;

export const getActionBasedActionListTemplate = gql`
    query actionBasedActionList($clinicId: MongoDbObjectId!, $filters: ActionBasedActionListFilters) {
        actionBasedActionList(clinicId: $clinicId, filters: $filters) {
            _id
            name
            action
            mainCategory
            subCategory
            number
            internalCode
            price
            tiotandvardPrice
            vat
            deliveryTime
            materialGroup
            materialMacros {
                _id
                name
            }
            enabled
            productionTemplate {
                planningDigitalTemplateId {
                    _id
                    name
                }
                planningPhysicalTemplateId {
                    _id
                    name
                }
                valueDigitalTemplateId {
                    _id
                    name
                }
                valuePhysicalTemplateId {
                    _id
                    name
                }
                productionDeductionValue
            }
        }
    }
`;

export const getMaterialGroupsTemplate = gql`
    query materialGroups($clinicId: MongoDbObjectId!) {
        materialGroups(clinicId: $clinicId) {
            materials {
                priceDiff
                material
                materialName
            }
            materialGroupId
            materialGroupName
        }
    }
`;

export const getClientSearchTemplate = gql`
    query clientSearch($filters: OrganizationsFilters!, $groupedByParent: Boolean) {
        clientSearch(filters: $filters, groupedByParent: $groupedByParent) {
            _id
            invoiceEnabled
            ptjCustomerNumber
            transportProvider {
                channel
                externalId
                code
            }
            isActive
            customerId
            customer {
                _id
                active
                materialListName
                actionListName
                name
                ptjCustomerNumber
                isWhiteLabeled
                organizationNumber
                ptjCustomerNumber
                isMain
                customerType
                isTransportEnabled
                parentClinicId
                parentClinic {
                    name
                }
            }
        }
    }
`;

export const getSupplierSearchTemplate = gql`
    query supplierSearch($filters: OrganizationsFilters) {
        supplierSearch(filters: $filters) {
            _id
            customerId
            isActive
            lab {
                _id
                name
                active
                customerType
                contact {
                    address {
                        ...orgAddressFields
                    }
                    communication {
                        phone
                        email
                        site
                    }
                }
            }
        }
    }
    ${orgAddressFragment}
`;
